import React, { useState } from 'react';
import { Input, Form, Button, Row, Col, DatePicker, InputNumber, Divider, notification, Spin } from 'antd';
import { handleFormChange, layout, validateMessages } from '../../../shared/Utils';
import { Typography } from 'antd';
import { useCreateRequestBookingMutation } from '../../../GraphQL/hooks/useCreateRequestBookingsMutation';
import { RequestBooking } from '../../../shared/types';
import { useUserState } from '../../../Hooks/useUserState';

const CreateRequestBooking = () => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const [status, setStatus] = useState<string>('');
    const { ...userState } = useUserState();
    const { mutation, loading: creating } = useCreateRequestBookingMutation();

    const onFinish = async (values) => {
        try {
            await mutation({
                ...values,
                status,
                operatorId: userState.operatorId,
            })
                .finally(() => {
                    notification.success({
                        message: 'Request booking created successfully',
                        description: 'The booking has been created successfully.',
                    });
                })
                .catch((err) => {
                    notification.error({
                        message: 'Failed to create request booking',
                        description: 'An error occurred while creating the request booking.',
                    });

                    console.error('Failed to create request booking:', err);
                });
        } catch (error) {
            console.error('Submission error:', error);

            notification.error({
                message: 'Booking creation failed',
                description: 'An error occurred while saving the booking. Please try again.',
            });
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Spin spinning={creating}>
            <Form<RequestBooking>
                {...layout}
                form={form}
                name="nest-messages"
                autoComplete="off"
                onFieldsChange={() => setFormError(handleFormChange(form))}
                onFinish={onFinish}
                validateMessages={validateMessages}
            >
                {/* Wrapper Row to Center Columns */}
                <Row justify="center">
                    <Col xs={24} md={20} lg={16}>
                        <Row gutter={32}>
                            {/* Left Column: Trip Details */}
                            <Col xs={24} md={12}>
                                <Typography.Title level={4}>Trip Details</Typography.Title>

                                <Form.Item
                                    name="pickUpLocation"
                                    label="Pick Location"
                                    rules={[{ required: true, message: 'Please enter the pickup location' }]}
                                >
                                    <Input placeholder="Enter pickup location" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="dropOffLocation"
                                    label="Drop Location"
                                    rules={[{ required: true, message: 'Please enter the drop location' }]}
                                >
                                    <Input placeholder="Enter drop location" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="tripDate"
                                    label="Date & Time"
                                    rules={[{ required: true, message: 'Please select date and time' }]}
                                >
                                    <DatePicker showTime style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item label="Total Passenger" name="nbrePassenger">
                                    <InputNumber
                                        type="number"
                                        defaultValue={1}
                                        min={1}
                                        max={9}
                                        style={{ width: '50%' }}
                                    />
                                </Form.Item>

                                <Form.Item label="Add Luggage" name="nbreluggage">
                                    <InputNumber
                                        type="number"
                                        defaultValue={0}
                                        min={0}
                                        max={9}
                                        style={{ width: '50%' }}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Right Column: Passenger Details */}
                            <Col xs={24} md={12}>
                                <Typography.Title level={4}>Passenger Details</Typography.Title>

                                <Form.Item
                                    name="passengerName"
                                    label="Full Name"
                                    rules={[{ required: true, message: 'Please enter your full name' }]}
                                >
                                    <Input placeholder="Enter full name" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="passengerEmail"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Enter a valid email' }]}
                                >
                                    <Input placeholder="example@mail.com" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="passengerPhoneNumber"
                                    label="Contact No."
                                    rules={[{ required: true, message: 'Please enter contact number' }]}
                                >
                                    <Input placeholder="Enter contact number" style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item name="note" label="Add Note">
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Additional instructions"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Buttons Centered Below */}
                <Row justify="center" style={{ marginTop: 32 }}>
                    <Form.Item>
                        <div style={{ display: 'inline-flex', gap: 16 }}>
                            <Button type="default" onClick={onReset}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" disabled={formError}>
                                Submit Request
                            </Button>
                        </div>
                    </Form.Item>
                </Row>
            </Form>
        </Spin>
    );
};

export default CreateRequestBooking;
